import React, { useCallback, useEffect, useRef, useState } from "react";
import useApi from "@/hooks/useApi";

interface LogEntry {
  id: string; // Table Storage RowKey (reverse-ticks + guid)
  timestamp: string;
  level: string | null;
  logger: string | null;
  message: string | null;
  environment: string | null;
  siteId: number | null;
  integrationTag: string | null;
  source: string | null; // "velaro-messaging-Staging", "livefluence-Production", etc.
}

const LEVELS = ["ERROR", "WARN", "INFO", "DEBUG"];

const LEVEL_COLORS: Record<string, string> = {
  ERROR: "bg-red-100 text-red-700 border-red-300",
  WARN:  "bg-yellow-100 text-yellow-700 border-yellow-300",
  INFO:  "bg-blue-100 text-blue-700 border-blue-300",
  DEBUG: "bg-slate-100 text-slate-500 border-slate-300",
};

function levelBadge(level: string | null) {
  const l = (level ?? "").toUpperCase();
  const cls = LEVEL_COLORS[l] ?? "bg-slate-100 text-slate-500 border-slate-300";
  return (
    <span className={`inline-block text-xs font-mono font-semibold px-1.5 py-0.5 rounded border ${cls}`}>
      {l || "?"}
    </span>
  );
}

function shortLogger(logger: string | null): string {
  if (!logger) return "";
  const parts = logger.split(".");
  return parts[parts.length - 1] ?? logger;
}

function highlightTags(message: string | null): React.ReactNode {
  if (!message) return null;
  const parts = message.split(/(\[(?:Skill|HttpNode)\])/gi);
  return parts.map((p, i) =>
    /^\[(?:Skill|HttpNode)\]$/i.test(p)
      ? <span key={i} className="bg-purple-100 text-purple-700 rounded px-1 font-semibold text-xs">{p}</span>
      : p
  );
}

function exportCsv(entries: LogEntry[]) {
  const header = "id,timestamp,level,logger,siteId,integrationTag,message\n";
  const rows = entries.map(e =>
    [e.id, e.timestamp, e.level ?? "", e.logger ?? "", e.siteId ?? "", e.integrationTag ?? "",
     `"${(e.message ?? "").replace(/"/g, '""')}"`].join(",")
  ).join("\n");
  const blob = new Blob([header + rows], { type: "text/csv" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `velaro-logs-${new Date().toISOString().slice(0, 16)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function VelaroLogger() {
  const api = useApi();
  const [entries, setEntries] = useState<LogEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [q, setQ] = useState("");
  const [qInput, setQInput] = useState("");
  const [level, setLevel] = useState("");
  const [loggerFilter, setLoggerFilter] = useState("");
  const [loggerInput, setLoggerInput] = useState("");
  const [siteId, setSiteId] = useState("");
  const [integration, setIntegration] = useState("");
  const [from, setFrom] = useState(() => {
    const d = new Date();
    d.setHours(d.getHours() - 2);
    return d.toISOString().slice(0, 16);
  });
  const [to, setTo] = useState(() => new Date().toISOString().slice(0, 16));
  const [take, setTake] = useState(200);
  const [availableIntegrations, setAvailableIntegrations] = useState<string[]>([]);
  const [availableSources, setAvailableSources] = useState<string[]>([]);
  const [source, setSource] = useState("");
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const autoRefreshRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [autoRefresh, setAutoRefresh] = useState(false);

  const fetchFilters = useCallback(async () => {
    try {
      const resp = await api.messaging.get("superadmin/logs/filters");
      const data = await resp.json();
      setAvailableIntegrations(Array.isArray(data.integrations) ? data.integrations : []);
      setAvailableSources(Array.isArray(data.sources) ? data.sources : []);
    } catch {
      setAvailableIntegrations([]);
      setAvailableSources([]);
    }
  }, [api]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ take: String(take) });
      if (q) params.set("q", q);
      if (level) params.set("level", level);
      if (loggerFilter) params.set("logger", loggerFilter);
      if (siteId) params.set("siteId", siteId);
      if (integration) params.set("integration", integration);
      if (source) params.set("source", source);
      if (from) params.set("from", new Date(from).toISOString());
      if (to) params.set("to", new Date(to).toISOString());

      const resp = await api.messaging.get(`superadmin/logs/search?${params}`);
      const data = await resp.json();
      setEntries(Array.isArray(data) ? data : []);
    } catch {
      setEntries([]);
    } finally {
      setLoading(false);
    }
  }, [api, q, level, loggerFilter, siteId, integration, from, to, take]);

  useEffect(() => {
    fetchFilters();
  }, [fetchFilters]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  useEffect(() => {
    if (autoRefreshRef.current) clearInterval(autoRefreshRef.current);
    if (autoRefresh) {
      autoRefreshRef.current = setInterval(() => {
        setTo(new Date().toISOString().slice(0, 16));
        fetchLogs();
      }, 5000);
    }
    return () => { if (autoRefreshRef.current) clearInterval(autoRefreshRef.current); };
  }, [autoRefresh, fetchLogs]);

  function handleSearch(e: React.FormEvent) {
    e.preventDefault();
    setQ(qInput);
    setLoggerFilter(loggerInput);
  }

  function handleClear() {
    setQ("");
    setQInput("");
    setLoggerFilter("");
    setLoggerInput("");
    setLevel("");
    setSiteId("");
    setIntegration("");
    setSource("");
  }

  return (
    <div className="p-6 max-w-7xl">
      <div className="flex items-start justify-between mb-4 gap-4">
        <div>
          <p className="text-sm text-slate-500">
            Self-hosted log search — receiver: <code className="text-xs bg-slate-100 px-1 rounded">velaro-messaging-api-staging /logs/inputs/…</code>
          </p>
          <p className="text-xs text-slate-400 mt-0.5">
            {entries.length} result{entries.length !== 1 ? "s" : ""}
            {loading && <span className="ml-2 text-blue-500">Loading…</span>}
          </p>
        </div>
        <div className="flex gap-2 items-center flex-wrap">
          <button
            onClick={() => exportCsv(entries)}
            disabled={entries.length === 0}
            className="text-xs px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50 disabled:opacity-40"
          >
            Export CSV
          </button>
          <label className="flex items-center gap-1.5 text-xs text-slate-600 cursor-pointer">
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={e => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            Live tail (5s)
          </label>
          <button
            onClick={fetchLogs}
            className="text-xs px-3 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Filters */}
      <form onSubmit={handleSearch} className="bg-slate-50 border border-slate-200 rounded-lg p-4 mb-5 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <div className="col-span-2 lg:col-span-2">
          <label className="block text-xs text-slate-500 mb-1">Search message</label>
          <input
            type="text"
            className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="[Skill], NetSuite, error text…"
            value={qInput}
            onChange={e => setQInput(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Logger</label>
          <input
            type="text"
            className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="e.g. NetSuiteSkill"
            value={loggerInput}
            onChange={e => setLoggerInput(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Level</label>
          <select
            className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={level}
            onChange={e => setLevel(e.target.value)}
          >
            <option value="">All levels</option>
            {LEVELS.map(l => <option key={l} value={l}>{l}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Integration tag</label>
          <select
            className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={integration}
            onChange={e => setIntegration(e.target.value)}
          >
            <option value="">All integrations</option>
            {availableIntegrations.map(i => <option key={i} value={i}>{i}</option>)}
            {/* Always show these even if no data yet */}
            {!availableIntegrations.includes("[Skill]") && <option value="[Skill]">[Skill]</option>}
            {!availableIntegrations.includes("[HttpNode]") && <option value="[HttpNode]">[HttpNode]</option>}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Source app</label>
          <select
            className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={source}
            onChange={e => setSource(e.target.value)}
          >
            <option value="">All sources</option>
            {availableSources.map(s => <option key={s} value={s}>{s}</option>)}
            {availableSources.length === 0 && (
              <>
                <option value="velaro-messaging-Staging">velaro-messaging-Staging</option>
                <option value="velaro-messaging-Production">velaro-messaging-Production</option>
                <option value="livefluence-Staging">livefluence-Staging</option>
                <option value="livefluence-Production">livefluence-Production</option>
              </>
            )}
          </select>
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Site ID</label>
          <input
            type="text"
            className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            placeholder="e.g. 1234"
            value={siteId}
            onChange={e => setSiteId(e.target.value.replace(/\D/g, ""))}
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">From</label>
          <input
            type="datetime-local"
            className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={from}
            onChange={e => setFrom(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">To</label>
          <input
            type="datetime-local"
            className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={to}
            onChange={e => setTo(e.target.value)}
          />
        </div>
        <div>
          <label className="block text-xs text-slate-500 mb-1">Max rows</label>
          <select
            className="w-full border border-slate-300 rounded px-2.5 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400"
            value={take}
            onChange={e => setTake(Number(e.target.value))}
          >
            {[50, 100, 200, 500].map(n => <option key={n} value={n}>{n}</option>)}
          </select>
        </div>
        <div className="col-span-2 lg:col-span-4 flex gap-2 justify-end">
          <button
            type="button"
            onClick={handleClear}
            className="text-xs px-3 py-1.5 border border-slate-300 rounded hover:bg-slate-50"
          >
            Clear
          </button>
          <button
            type="submit"
            className="text-xs px-4 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Search
          </button>
        </div>
      </form>

      {/* Results table */}
      <div className="border border-slate-200 rounded-lg overflow-hidden text-sm">
        <table className="w-full text-left border-collapse">
          <thead className="bg-slate-100 text-xs text-slate-500 uppercase tracking-wide">
            <tr>
              <th className="px-3 py-2 w-36">Timestamp</th>
              <th className="px-3 py-2 w-16">Level</th>
              <th className="px-3 py-2 w-32">Logger</th>
              <th className="px-3 py-2 w-20">Site</th>
              <th className="px-3 py-2 w-24">Tag</th>
              <th className="px-3 py-2">Message</th>
            </tr>
          </thead>
          <tbody>
            {entries.length === 0 && (
              <tr>
                <td colSpan={6} className="px-3 py-8 text-center text-slate-400">
                  {loading ? "Loading…" : "No results. Adjust filters or check that VELARO_LOGGER_ENABLED=true in Azure App Settings."}
                </td>
              </tr>
            )}
            {entries.map(entry => {
              const isExpanded = expandedId === entry.id;
              const ts = new Date(entry.timestamp);
              return (
                <tr
                  key={entry.id}
                  className={`border-t border-slate-100 cursor-pointer hover:bg-slate-50 transition-colors ${
                    entry.level === "ERROR" ? "bg-red-50" : entry.level === "WARN" ? "bg-yellow-50" : ""
                  }`}
                  onClick={() => setExpandedId(isExpanded ? null : entry.id)}
                >
                  <td className="px-3 py-1.5 font-mono text-xs text-slate-500 whitespace-nowrap">
                    {ts.toLocaleString()}
                  </td>
                  <td className="px-3 py-1.5">{levelBadge(entry.level)}</td>
                  <td className="px-3 py-1.5 text-xs text-slate-600 font-mono truncate max-w-[8rem]" title={entry.logger ?? undefined}>
                    {shortLogger(entry.logger)}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-slate-500">
                    {entry.siteId ?? <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-3 py-1.5">
                    {entry.integrationTag ? (
                      <span className="text-xs bg-purple-50 text-purple-700 border border-purple-200 rounded px-1.5 py-0.5 font-medium">
                        {entry.integrationTag}
                      </span>
                    ) : <span className="text-slate-300 text-xs">—</span>}
                  </td>
                  <td className="px-3 py-1.5 text-xs text-slate-700">
                    {isExpanded ? (
                      <div className="whitespace-pre-wrap font-mono bg-slate-900 text-slate-100 rounded p-2 mt-1 text-xs leading-relaxed">
                        {entry.message}
                      </div>
                    ) : (
                      <span className="truncate block max-w-xl" title={entry.message ?? undefined}>
                        {highlightTags(entry.message)}
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {entries.length > 0 && (
        <p className="text-xs text-slate-400 mt-2 text-right">
          Showing {entries.length} rows. Click row to expand full message.
        </p>
      )}
    </div>
  );
}
